import type { Ref } from 'react'
import { NavLink } from 'react-router-dom'
import { PRODUCT_NAME, PRODUCT_TAGLINE } from '../pages'
import { resolvedTheme, type ThemeChoice } from '../state/theme'
import { Button, buttonClass } from './Button'
import { Icon } from './Icon'

/** The bar across the top: the menu toggle, the product's name, what is happening now, help and theme. */
export function Topbar({
  restoring,
  busy,
  online,
  theme,
  onTheme,
  onHelp,
  onFeedback,
  onGuide,
  guideRemaining = 0,
  navOpen,
  onToggleNav,
  navToggleRef,
  home,
}: {
  restoring: boolean
  busy: boolean
  online: boolean
  theme: ThemeChoice
  onTheme: (theme: ThemeChoice) => void
  onHelp: () => void
  onFeedback?: () => void
  onGuide?: () => void
  /** Getting-started steps still to do; counted beside Guide until none are left. */
  guideRemaining?: number
  navOpen: boolean
  onToggleNav: () => void
  navToggleRef: Ref<HTMLButtonElement>
  /** Where the brand leads: the overview, or Welcome while nothing is loaded. */
  home: string
}) {
  const shown = resolvedTheme(theme)
  return (
    <header className="topbar">
      <div className="topbar-left">
        <button
          ref={navToggleRef}
          type="button"
          className="nav-toggle"
          aria-expanded={navOpen}
          aria-label={navOpen ? 'Close navigation menu' : 'Open navigation menu'}
          onClick={onToggleNav}
        >
          <Icon name={navOpen ? 'close' : 'list'} size={16} />
          <span>Menu</span>
        </button>
        <div className="mobile-brand-link">
          <Brand to={home} />
        </div>
        {/* The product's name; the sidebar has room only for the EVERSE logo. The
            page and its section are the H1 and the active navigation item. */}
        <span className="topbar-product">{PRODUCT_NAME}</span>
        <div className="topbar-meta">
          {/* Only what is happening right now. How many assessments are loaded
              belongs to the session, so it is shown on the Data entry in the
              navigation, beside the page that manages them. */}
          {restoring && (
            <span className="session-status-pill status-loading">
              <span className="status-dot" />
              <span>Restoring session...</span>
            </span>
          )}
          {busy && !restoring && (
            <span className="session-status-pill status-busy">
              <span className="status-dot" />
              <span>Reading...</span>
            </span>
          )}
          {!online && (
            <span
              className="session-status-pill status-offline"
              title="Loaded reports still work offline"
            >
              <span className="status-dot" />
              <span>Offline</span>
            </span>
          )}
        </div>
      </div>
      <div className="topbar-actions">
        {onGuide && (
          <button
            type="button"
            className={`${buttonClass({ tone: 'quiet', size: 'small' })} guide-toggle-btn`}
            title="Getting started"
            onClick={onGuide}
          >
            <Icon name="listChecks" size={14} />
            <span>Guide</span>
            {guideRemaining > 0 && (
              <span className="count-badge">
                {' '}
                {guideRemaining}
                <span className="visually-hidden">
                  {guideRemaining === 1 ? ' step to do' : ' steps to do'}
                </span>
              </span>
            )}
          </button>
        )}
        <Button
          tone="quiet"
          size="small"
          icon="message"
          label="Feedback"
          className="feedback-toggle-btn"
          title="Share feedback or report an issue"
          onClick={onFeedback}
        />
        <Button
          tone="quiet"
          size="small"
          icon="help"
          label="Help"
          className="help-toggle-btn"
          title="Guides that show you where to click"
          onClick={onHelp}
        />
        <button
          type="button"
          className={`theme-toggle-btn theme-${shown}`}
          title={shown === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          aria-label={shown === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          onClick={() => onTheme(shown === 'dark' ? 'light' : 'dark')}
        >
          <Icon name={shown === 'dark' ? 'sun' : 'moon'} size={14} />
          <span className="theme-toggle-tip">{shown === 'dark' ? 'Light' : 'Dark'}</span>
        </button>
      </div>
    </header>
  )
}

/** The product's mark: the EVERSE logo, and this application's name beside it. */
export function Brand({ to, onNavigate }: { to: string; onNavigate?: () => void }) {
  return (
    <NavLink
      to={to}
      className="brand-link"
      onClick={onNavigate}
      title={`${PRODUCT_NAME}, ${PRODUCT_TAGLINE.toLowerCase()}`}
      aria-label={`${PRODUCT_NAME} home`}
    >
      <img
        src={`${import.meta.env.BASE_URL}brand/everse-full.svg`}
        alt="EVERSE"
        className="brand-logo brand-logo-light"
      />
      <img
        src={`${import.meta.env.BASE_URL}brand/everse-full-dark.svg`}
        alt="EVERSE"
        className="brand-logo brand-logo-dark"
      />
    </NavLink>
  )
}
